/**
 * The wardrobe (ตู้เสื้อผ้า, data/wardrobe.ts): free customization of how the player looks, apart from the creator.
 *
 * - **Wear** a fashion skin per slot (a whole outfit, or a hat, a top, a bottom, shoes, a back piece), or take it off:
 *   only one the character owns (`ownsSkin`: bought, granted, or free to all) and that fits their sex.
 * - **Show or hide** per slot: a fashion skin worn but hidden lets the gear under it show; a gear slot hidden shows
 *   neither its piece nor (with no fashion there) anything but the everyday clothes.
 * - **Dye** a piece per channel ('#rrggbb'), or clear it back to the garment's own colour.
 * - **Saved sets** (ชุดบันทึก): the skins worn, what is hidden and the dyes, saved in one of `WARDROBE_SETS` places and
 *   put back on with one command; a skin no longer owned is left off.
 *
 * All saved in `progress.wardrobe` (absent until first changed) and `progress.skinWorn`. Purely cosmetic: nothing here
 * changes a fight, so nothing reads the World's random stream. `wardrobeLook` is what the figure should show, for the
 * client's parts (render3d2 koParts `partsOf`).
 */
import type { Sex } from '../data/appearance';
import { type EquipSlot, isEquipSlot } from '../data/items';
import { coversOf, ownsSkin, SKIN_SLOTS, SKINS, skinFits, type SkinSlot } from '../data/skins';
import {
  GEAR_PIECE,
  GEAR_SHOW_SLOTS,
  isDyeColor,
  isDyeKey,
  newWardrobe,
  SET_NAME_MAX,
  WARDROBE_SETS,
  type WardrobeSave,
  type WardrobeSet,
} from '../data/wardrobe';
import type { PlayerProgress } from '../progress';
import type { SystemHost, WorldSystem } from './types';
import { lookOf } from '../world';

/** Commands (part of `PlayerCommand`). */
export type WardrobeCommand =
  /** Wear an owned skin in its slot, or take off what is worn there (null). */
  | { t: 'wardrobeWear'; slot: SkinSlot; id: string | null }
  /** Show or hide a fashion slot (`kind: 'fashion'`, a `SkinSlot`) or a gear slot (`'gear'`, an `EquipSlot`). */
  | { t: 'wardrobeShow'; kind: 'fashion' | 'gear'; slot: string; show: boolean }
  /** Dye a channel (`key`: `<piece>/<channel>`, data/wardrobe.ts `dyeKey`) a colour, or clear it (null). */
  | { t: 'wardrobeDye'; key: string; color: string | null }
  /** Save what is worn now in set `index` (0-based), named `name` (or kept / `ชุดที่ n`). */
  | { t: 'wardrobeSaveSet'; index: number; name?: string }
  /** Put on set `index`. */
  | { t: 'wardrobeLoadSet'; index: number }
  /** Rename set `index`. */
  | { t: 'wardrobeRenameSet'; index: number; name: string }
  /** Forget set `index`. */
  | { t: 'wardrobeClearSet'; index: number };

/** The wardrobe of a save (the defaults while it has none). */
export const wardrobeOf = (pr: PlayerProgress): WardrobeSave => pr.wardrobe ?? newWardrobe();

/** The skin shown in a fashion slot: the one worn there unless it is hidden. */
export function shownSkin(pr: PlayerProgress, slot: SkinSlot): string | null {
  const id = pr.skinWorn[slot];
  return id && !pr.wardrobe?.hideFashion.includes(slot) ? id : null;
}

/** Whether a gear slot's piece shows on the figure. */
export const gearShown = (pr: PlayerProgress, slot: EquipSlot): boolean =>
  !pr.wardrobe?.hideGear.includes(slot);

/** What one piece slot of the figure shows: a fashion skin (a piece, or the whole outfit covering it), else gear. */
export interface PieceLook {
  /** The skin shown there (a piece skin, or the whole outfit's id when it covers this slot); null for none. */
  skin: string | null;
  /** The equipment key shown there (`progress.equipment`), when no skin is and the gear is not hidden. */
  gear: string | null;
}

/** What the player's figure shows, slot by slot (for the client's parts). */
export interface WardrobeLook {
  sex: Sex;
  /** The whole fashion outfit shown, if any. */
  outfit: string | null;
  /** The piece slots; `hair` shows a hairstyle skin or nothing (null: the creator's own hair). */
  pieces: Record<'hat' | 'hair' | 'top' | 'bottom' | 'shoes' | 'cape' | 'shoulders', PieceLook>;
  /** The back skin shown, if any. */
  back: string | null;
  /** The weapon shown (equipment key), null when hidden or none. */
  weapon: string | null;
  /** The dyes (`<piece>/<channel>` → colour). */
  dye: Readonly<Record<string, string>>;
}

/** The figure's look from a save: fashion over gear, slot by slot, honouring what is hidden. */
export function wardrobeLook(pr: PlayerProgress): WardrobeLook {
  const outfit = shownSkin(pr, 'outfit');
  const covered = new Set(outfit ? coversOf(outfit) : []);
  const gearIn = (slot: SkinSlot): string | null => {
    for (const [g, piece] of Object.entries(GEAR_PIECE) as [EquipSlot, SkinSlot][])
      if (piece === slot) return gearShown(pr, g) ? (pr.equipment[g] ?? null) : null;
    return null;
  };
  const piece = (slot: SkinSlot): PieceLook => {
    const own = shownSkin(pr, slot);
    if (own) return { skin: own, gear: null };
    if (outfit && covered.has(slot)) return { skin: outfit, gear: null };
    return { skin: null, gear: gearIn(slot) };
  };
  return {
    sex: pr.appearance.sex,
    outfit,
    pieces: {
      hat: piece('hat'),
      hair: piece('hair'),
      top: piece('top'),
      bottom: piece('bottom'),
      shoes: piece('shoes'),
      cape: piece('cape'),
      shoulders: piece('shoulders'),
    },
    back: shownSkin(pr, 'back'),
    weapon: gearShown(pr, 'weapon') ? (pr.equipment.weapon ?? null) : null,
    dye: wardrobeOf(pr).dye,
  };
}

const isSkinSlot = (v: unknown): v is SkinSlot =>
  typeof v === 'string' && (SKIN_SLOTS as readonly string[]).includes(v);
const isIndex = (i: unknown): i is number =>
  Number.isInteger(i) && (i as number) >= 0 && (i as number) < WARDROBE_SETS;
const cleanName = (s: string): string => s.replace(/\s+/g, ' ').trim().slice(0, SET_NAME_MAX);

export class WardrobeSystem implements WorldSystem {
  readonly name = 'wardrobe';

  constructor(private readonly h: SystemHost) {}

  private get pr(): PlayerProgress {
    return this.h.world.progress;
  }

  /** The save's wardrobe, made on first change. */
  private save(): WardrobeSave {
    return (this.pr.wardrobe ??= newWardrobe());
  }

  /** Whether the character may wear `id` in `slot`. */
  private canWear(slot: SkinSlot, id: string): boolean {
    const pr = this.pr;
    return SKINS[id]?.slot === slot && ownsSkin(pr.skins, id) && skinFits(id, pr.appearance.sex);
  }

  /** The figure shows what is worn now (the world's `outfitOf`: the looks, and the weapon put away if it is). */
  private refresh(): void {
    const p = this.h.world.player;
    const looks = lookOf(this.pr).outfit;
    p.outfit = p.sheathed ? [...looks, 'sheathed'] : looks;
    this.h.emit({ type: 'progressChanged' });
  }

  readonly commands = {
    wardrobeWear: (cmd: Extract<WardrobeCommand, { t: 'wardrobeWear' }>) => {
      if (!isSkinSlot(cmd.slot)) return;
      const pr = this.pr;
      if (cmd.id === null) {
        delete pr.skinWorn[cmd.slot];
        this.refresh();
        return;
      }
      if (typeof cmd.id !== 'string' || !this.canWear(cmd.slot, cmd.id)) return;
      pr.skinWorn[cmd.slot] = cmd.id;
      // Put on to be seen: a hidden slot shows again.
      const w = pr.wardrobe;
      if (w) w.hideFashion = w.hideFashion.filter((s) => s !== cmd.slot);
      this.refresh();
    },

    wardrobeShow: (cmd: Extract<WardrobeCommand, { t: 'wardrobeShow' }>) => {
      if (cmd.kind === 'fashion') {
        if (!isSkinSlot(cmd.slot)) return;
        const w = this.save();
        w.hideFashion = w.hideFashion.filter((s) => s !== cmd.slot);
        if (!cmd.show) w.hideFashion.push(cmd.slot);
      } else if (cmd.kind === 'gear') {
        const slot = cmd.slot;
        if (!isEquipSlot(slot) || !GEAR_SHOW_SLOTS.includes(slot)) return;
        const w = this.save();
        w.hideGear = w.hideGear.filter((s) => s !== slot);
        if (!cmd.show) w.hideGear.push(slot);
      }
    },

    wardrobeDye: (cmd: Extract<WardrobeCommand, { t: 'wardrobeDye' }>) => {
      if (typeof cmd.key !== 'string' || !isDyeKey(cmd.key)) return;
      if (cmd.color === null) {
        if (this.pr.wardrobe) delete this.pr.wardrobe.dye[cmd.key];
        return;
      }
      const c = typeof cmd.color === 'string' ? cmd.color.toLowerCase() : '';
      if (!isDyeColor(c)) return;
      this.save().dye[cmd.key] = c;
    },

    wardrobeSaveSet: (cmd: Extract<WardrobeCommand, { t: 'wardrobeSaveSet' }>) => {
      if (!isIndex(cmd.index)) return;
      const w = this.save();
      const named = typeof cmd.name === 'string' ? cleanName(cmd.name) : '';
      const set: WardrobeSet = {
        name: named || w.sets[cmd.index]?.name || `ชุดที่ ${cmd.index + 1}`,
        worn: { ...this.pr.skinWorn },
        hideFashion: [...w.hideFashion],
        hideGear: [...w.hideGear],
        dye: { ...w.dye },
      };
      w.sets[cmd.index] = set;
      this.h.notice(`บันทึก「${set.name}」แล้ว`);
    },

    wardrobeLoadSet: (cmd: Extract<WardrobeCommand, { t: 'wardrobeLoadSet' }>) => {
      if (!isIndex(cmd.index)) return;
      const w = this.save();
      const set = w.sets[cmd.index];
      if (!set) return;
      const pr = this.pr;
      let missing = 0;
      for (const slot of SKIN_SLOTS) {
        const id = set.worn[slot];
        if (!id) delete pr.skinWorn[slot];
        else if (this.canWear(slot, id)) pr.skinWorn[slot] = id;
        else {
          delete pr.skinWorn[slot];
          missing++;
        }
      }
      w.hideFashion = [...set.hideFashion];
      w.hideGear = [...set.hideGear];
      w.dye = { ...set.dye };
      this.h.notice(
        missing ? `สวม「${set.name}」แล้ว (ไม่มีบางชิ้นแล้ว ${missing} ชิ้น)` : `สวม「${set.name}」แล้ว`,
      );
    },

    wardrobeRenameSet: (cmd: Extract<WardrobeCommand, { t: 'wardrobeRenameSet' }>) => {
      if (!isIndex(cmd.index) || typeof cmd.name !== 'string') return;
      const set = this.pr.wardrobe?.sets[cmd.index];
      const name = cleanName(cmd.name);
      if (set && name) set.name = name;
    },

    wardrobeClearSet: (cmd: Extract<WardrobeCommand, { t: 'wardrobeClearSet' }>) => {
      if (!isIndex(cmd.index) || !this.pr.wardrobe) return;
      this.pr.wardrobe.sets[cmd.index] = null;
    },
  };
}
